import React from 'react';
import PropTypes from 'prop-types';
import tw, { styled } from 'twin.macro';
import { Icon } from 'semantic-ui-react';

export const ButtonText = styled.button`
  ${tw`text-sm tracking-wide border-none outline-none cursor-pointer focus:outline-none`};

  ${(props) => props.disabled && tw`opacity-50 pointer-events-none`};

  ${(props) =>
    props.type.includes('primary')
      ? tw`px-8 py-2 text-blue-500 bg-white`
      : tw`px-5 py-2 mt-0 text-base text-white bg-transparent border-2 border-solid rounded-md shadow-none`}

  ${(props) =>
    !props.type.includes('primary') &&
    `
    color: ${props.color ? props.color : 'white'};
    border-color: ${props.color ? props.color : 'white'};

    &:hover {
      color: ${props.color ? 'white' : 'rgba(0, 0, 0, .8)'};
      background-color: ${props.color ? props.color : 'white'};
      border-color: ${props.color ? props.color : 'white'};
    }
  `}
`;

export const ButtonTextIcon = styled(ButtonText)`
  .icon {
    ${tw`mr-2 -ml-1 text-base align-baseline`};
  }
`;

// const ButtonTextIconImage = styled.img`
//   ${tw`mr-2 w-2.5`};
// `;

export const ButtonIcon = styled.button`
  ${tw`box-border justify-center text-black bg-transparent border-none outline-none cursor-pointer opacity-90 hover:opacity-100 focus:outline-none`};

  ${(props) => props.disabled && tw`opacity-50 pointer-events-none`};
  border-color: ${(props) => (props.redBorder ? 'red' : 'initial')};
`;

export const ButtonIconLabel = styled.button`
  ${tw`flex w-full text-base font-bold tracking-wide border-none outline-none cursor-pointer opacity-90 hover:opacity-100 focus:outline-none`};
  padding: 0.78571429rem 1.5rem;
  border-radius: 0.28571429rem;

  ${(props) => props.disabled && tw`opacity-50 pointer-events-none`};

  .icon-wrapper {
    ${tw`mr-6`}
    margin-top: -0.78571429rem;
    margin-left: -1.5rem;
    margin-bottom: -0.78571429rem;
    padding: 0.78571429rem;
    border-radius: 0.28571429rem 0 0 0.28571429rem;
  }

  .icon {
    ${tw`m-0`};
  }

  .button-text {
    ${tw`flex-grow`}
  }
`;

// eslint-disable-next-line max-lines-per-function
export const Button = ({
  type,
  text,
  color,
  iconLink,
  onClick,
  disabled,
  className,
  style,
  iconSize,
  onMouseEnter,
  onMouseLeave,
  htmlFor,
  redCloseBtn,
}) => {
  switch (type) {
    case 'primary':
    case 'secondary':
      return (
        <ButtonText
          type={type}
          onClick={onClick}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          disabled={disabled}
          className={className}
          style={style}
          color={color}
          htmlFor={htmlFor}>
          {text}
        </ButtonText>
      );

    case 'primary icon':
    case 'secondary icon':
      return (
        <ButtonTextIcon
          type={type}
          onClick={onClick}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          disabled={disabled}
          className={className}
          style={style}
          htmlFor={htmlFor}
          color={color}>
          {/* {
          icon && (
            <ButtonTextIconImage src={iconLink} alt="button icon" />
          )
        } */}
          <Icon
            name={iconLink}
            color={color !== 'white' ? color : null}
            inverted={color === 'white'}
            size={iconSize || null}
          />
          <span>{text}</span>
        </ButtonTextIcon>
      );

    case 'icon':
      return (
        <ButtonIcon
          onClick={onClick}
          disabled={disabled}
          aria-label={text}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          className={className}
          style={style}
          color={color}
          htmlFor={htmlFor}
          redBorder={!!redCloseBtn}>
          <Icon
            name={iconLink}
            color={color !== 'white' ? color : null}
            inverted={color === 'white'}
            size={iconSize || null}
            circular={!!redCloseBtn}
          />
        </ButtonIcon>
      );

    case 'icon label':
      return (
        <ButtonIcon
          onClick={onClick}
          disabled={disabled}
          aria-label={text}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          className={className}
          style={style}
          color={color}
          htmlFor={htmlFor}>
          <span className="icon-wrapper">
            <Icon
              name={iconLink}
              color={color !== 'white' ? color : null}
              inverted={color === 'white'}
              size={iconSize || null}
            />
          </span>
          <span className="button-text">{text}</span>
        </ButtonIcon>
      );

    default:
      return null;
  }
};

Button.defaultProps = {
  text: '',
  iconLink: '',
  onClick: () => null,
  disabled: false,
  className: '',
  style: {},
  color: '',
  iconSize: '',
  htmlFor: '',
  redCloseBtn: false,
  onMouseEnter: () => null,
  onMouseLeave: () => null,
};

Button.propTypes = {
  type: PropTypes.string.isRequired,
  text: PropTypes.string,
  iconLink: PropTypes.string,
  onClick: PropTypes.func,
  onMouseEnter: PropTypes.func,
  onMouseLeave: PropTypes.func,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  htmlFor: PropTypes.string,
  color: PropTypes.string,
  style: PropTypes.objectOf(PropTypes.string),
  iconSize: PropTypes.string,
  redCloseBtn: PropTypes.bool,
};

export default {
  Button,
  ButtonTextIcon,
  ButtonText,
  ButtonIconLabel,
};
